<script setup lang="ts">
  import { ref, onMounted, computed, ComputedRef, defineAsyncComponent } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import { useVisitorsStore, useCoreStore, useFeaturesStore } from '@/shared/store'
  import { capitalizeText, is504 } from '@/shared/utils'
  import type { VisitorsPolicy } from '@/shared/types/response.types'
  import { useConvertUsd } from '@/shared/composables/useConvertUsd'
  import { useLinkTarget } from '@/shared/composables/useLinkTarget'
  import { useFeatureToggle } from '@/shared/composables/useFeatureToggle'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'
  import { PurchaseFlowRoutes } from '@/shared/types/router.types'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  import IconQC from '@qicgroupdoha/qic-connect-ds/components/IconQC'
  import InboundPolicies from './components/InboundPolicies.vue'
  import TravelKit from './components/TravelKit/TravelKit.vue'
  import RedirectLoader from '@/shared/components/rebranding/RedirectLoader.vue'
  import { FlowWidget } from '@/widgets'
  import { FlowCard } from '@/shared/components/rebranding/FlowCard'
  import { BaseError } from '@/shared/components/rebranding/BaseError'
  import { sha256 } from '@/shared/utils/crypto'

  const DomainAnnotation = defineAsyncComponent(
    () => import('@/shared/components/rebranding/DomainAnnotation.vue')
  )

  const VisitorPolicy = defineAsyncComponent(
    () => import('@/shared/components/rebranding/VisitorPolicy/VisitorPolicy.vue')
  )

  const router = useRouter()
  const route = useRoute()
  const visitorsStore = useVisitorsStore()
  const coreStore = useCoreStore()
  const { t, locale } = useI18n()
  const gaService = useGoogleAnalytics()
  const featuresStore = useFeaturesStore()
  const { initFeatureToggle } = useFeatureToggle()

  const email = ref(visitorsStore.holder.email)
  const link = ref<string>('')
  const receipt = ref<Nullable<string>>(null)
  const policies = ref<VisitorsPolicy[]>([])
  const errorMessage = ref<Nullable<string>>(null)
  const error504 = ref<boolean>(false)
  const loading = ref<boolean>(false)
  const quoteno = ref<Nullable<string>>(null)
  const policyno = ref<Nullable<string>>(null)
  const hasUninsuredUsers = ref<boolean>(false)
  const continuePurchaseForOthersText = ref('')
  const inboundPolicy = ref('')
  const inboundReceipt = ref('')
  const entryType = visitorsStore.getAnalyticsEntryType
  const price = localStorage.getItem('user-payload.price')
  const paymentMethod = localStorage.getItem('paymentMethod')
  const usdPrice = useConvertUsd(price)
  const { getLinkTarget } = useLinkTarget()

  const multiplePolicies = computed(() => policies.value.length > 1)
  const isTravelKitSectionVisible = computed(
    () => featuresStore.isTravelKitOfferEnabled || featuresStore.isOoredooEnabled
  )
  const containerMaxWidth = computed(() => (isTravelKitSectionVisible.value ? 600 : undefined))
  const isPolicePaidWithProblems: ComputedRef<boolean> = computed(
    () => route.query.paymentreceived === 'true'
  )
  const headerText = computed(() => {
    if (isPolicePaidWithProblems.value) return t('visitors.success_page.short.header')
    return multiplePolicies.value
      ? t('qic_insurance_success_header_second')
      : t('qic_insurance_success_header')
  })

  const loadPolicy = async () => {
    await visitorsStore
      .getPolicy()
      .then((data) => {
        link.value = data.visitorPolicies[0].link
        email.value = data.visitorPolicies[0].email
        receipt.value = data.visitorPolicies[0].receiptlink
        policyno.value = data.policyNo
        policies.value = data.visitorPolicies
        if (data.inboundVisitorsPolicy && data.inboundVisitorsReceipt) {
          inboundPolicy.value = data.inboundVisitorsPolicy
          inboundReceipt.value = data.inboundVisitorsReceipt
        }
        localStorage.setItem('user-payload.policyno', policyno.value)
      })
      .catch(async (err) => {
        if (is504(err)) {
          error504.value = true
          errorMessage.value = err.message
        }

        if (quoteno.value !== null) {
          await visitorsStore
            .getQuotePayments(quoteno.value)
            .then(({ payCount }) => {
              if (payCount === 0) {
                errorMessage.value = err.message
              }
              policyno.value = localStorage.getItem('user-payload.policyno')
            })
            .catch((err) => {
              if (is504(err)) {
                error504.value = true
                errorMessage.value = err.message
              }
              errorMessage.value = err.message
            })
        }
      })
  }

  const getFilesAsArchiveFor = (filesType: string) =>
    `/api/v2/getvisitorspdfs?quoteno=${quoteno.value}&type=${filesType}&policyno=${policyno.value}`

  const sendPurchaseEndEvent = () => {
    let usedPolicyIdList = JSON.parse(localStorage.getItem('used-policy-id') || '[]')
    if (usedPolicyIdList.includes(policyno.value)) {
      return
    }

    if (usedPolicyIdList.length >= 10) {
      usedPolicyIdList = []
    }

    usedPolicyIdList.push(policyno.value)
    localStorage.setItem('used-policy-id', JSON.stringify(usedPolicyIdList))

    gaService.sendPurchaseEnd(quoteno.value, usdPrice)
  }

  const removeTravellers = async () => {
    if (localStorage.getItem('user-payload.blockRemoveTravellers')) {
      return
    }

    await visitorsStore.removeTravellers(hasUninsuredUsers.value)
    localStorage.setItem('user-payload.blockRemoveTravellers', 'true')
  }

  const onContinue = () => {
    localStorage.removeItem('user-payload.uninsuredUsersCount')
    gaService.sendClickProceedBySuccessPage(policyno.value, coreStore.getInitialReferrer)

    window.open(
      router.resolve({
        name: PurchaseFlowRoutes.TRAVEL_DATE,
      }).href
    )
  }

  const sendDownloadEvent = (policy = true, forAll = false, section?: string) => {
    let event: string
    policy
      ? (event = forAll ? 'click_download_all_policies' : 'click_download_policy')
      : (event = forAll ? 'click_all_payment_receipt' : 'click_payment_receipt')
    gaService.sendPolicyEvents(
      event,
      'downloading_policy',
      String(policyno.value),
      undefined,
      undefined,
      section
    )
  }

  const setContinuePurchaseForOthersText = () => {
    if (!visitorsStore.visitorsList.length) {
      return
    }

    let text = ''
    let plural = 1
    if (visitorsStore.visitorsList.length === 1) {
      if (!visitorsStore.visitorsList[0].firstName) {
        return
      }

      text = `${visitorsStore.visitorsList[0].firstName} ${visitorsStore.visitorsList[0].lastName}`
    } else {
      text = `${visitorsStore.visitorsList.length}`
      plural = visitorsStore.visitorsList.length
    }

    continuePurchaseForOthersText.value = t(
      'qic_insurance_success_any_uninsured_label',
      { text: capitalizeText(text) },
      plural
    )
  }

  const onReload = () => router.go(0)

  onMounted(async () => {
    if (isPolicePaidWithProblems.value) {
      return
    }
    try {
      loading.value = true
      const uninsuredUsersCount = Number(localStorage.getItem('user-payload.uninsuredUsersCount'))
      hasUninsuredUsers.value = uninsuredUsersCount > 0
      quoteno.value = localStorage.getItem('user-payload.quoteno')

      const [phone, email] = await Promise.all([
        sha256(visitorsStore.holder.phoneInfo.phone || ''),
        sha256(visitorsStore.holder.email || ''),
        loadPolicy(),
        initFeatureToggle(),
      ])

      gaService.sendViewDownloadPolicy(policyno.value, policies.value.length)
      gaService.sendPurchase(
        entryType,
        quoteno.value,
        policyno.value,
        price,
        paymentMethod,
        !!inboundPolicy.value,
        visitorsStore.inboundPrice || 0,
        phone,
        email,
      )

      if (!errorMessage.value) {
        await removeTravellers()
        sendPurchaseEndEvent()
        setContinuePurchaseForOthersText()
      }
    } finally {
      loading.value = false
    }
  })
</script>

<template>
  <div class="insurance-success">
    <FlowWidget v-if="!loading" :with-arrow-back="false" disable-progress-bar>
      <div v-if="!errorMessage" class="insurance-success__wrapper">
        <FlowCard :max-width="containerMaxWidth" :with-button="false">
          <div
            class="insurance"
            :class="{ 'insurance--single': !Boolean(continuePurchaseForOthersText) }"
          >
            <div class="insurance-success__icon">
              <!-- @vue-ignore -->
              <IconQC name="check" :color="$qicConnect.colors.icn.srf.successGreen" :size="36" />
            </div>
            <div class="insurance-success__text">
              <h2 class="heading-2 insurance-success__title">{{ headerText }}</h2>

              <div v-if="isPolicePaidWithProblems" class="insurance-success__email text-medium">
                <p>
                  {{
                    $t('visitors.success_page.short.annotation', { email: 'email' }).split(
                      'email'
                    )[0]
                  }}
                  <span class="insurance-success__email-text">{{ email }}</span>
                  {{
                    $t('visitors.success_page.short.annotation', { email: 'email' }).split(
                      'email'
                    )[1]
                  }}
                </p>
              </div>

              <div v-if="multiplePolicies" class="insurance-success__email text-medium">
                <div>
                  {{ $t('qic_insuranse_success_annotation') }}
                </div>
                {{ $t('qic_insurance_email_second') }}
              </div>
              <div v-else-if="receipt" class="insurance-success__email text-medium">
                <div>
                  {{ $t('qic_insuranse_success_annotation') }}
                </div>
                <div>
                  {{
                    inboundPolicy
                      ? $t('qic_insurance_email_second')
                      : $t('qic_insurance_email_first')
                  }}
                </div>
                <span class="insurance-success__email-text"> &nbsp; {{ email }}</span>
              </div>
            </div>

            <template v-if="!isPolicePaidWithProblems">
              <div v-if="continuePurchaseForOthersText" class="insurance-success-uninsured">
                <div class="paragraph-default-bold">
                  {{ continuePurchaseForOthersText }}
                </div>

                <ButtonQC
                  variant="primary"
                  size="large"
                  wide
                  :icon-after-content="locale === 'en' ? 'arrow-narrow-right' : 'arrow-narrow-left'"
                  data-test="proceed-button"
                  @click="onContinue"
                >
                  {{ $t('qic_insurance_success_proceed') }}
                </ButtonQC>
              </div>
              <template v-if="!inboundPolicy">
                <div v-if="!multiplePolicies" class="insurance-single-policy">
                  <template v-if="false && continuePurchaseForOthersText">
                    <ButtonQC
                      icon="file-download"
                      variant="secondary"
                      size="large"
                      data-test="download-policy-button"
                      :href="policies[0]?.link || ''"
                      :target="getLinkTarget()"
                      @click="sendDownloadEvent"
                    >
                      {{ $t('qic_insurance_success_download_policy') }}
                    </ButtonQC>
                  </template>
                  <template v-else>
                    <ButtonQC
                      icon="file-download"
                      variant="secondary"
                      size="large"
                      data-test="download-policy-button"
                      :href="policies[0]?.link || ''"
                      :target="getLinkTarget()"
                      @click="sendDownloadEvent"
                    >
                      {{ $t('visitors.success_page.one_policy.download_policy_button') }}
                    </ButtonQC>

                    <ButtonQC
                      icon="file"
                      variant="secondary"
                      size="large"
                      data-test="download-receipt-button"
                      :href="policies[0]?.receiptlink || ''"
                      :target="getLinkTarget()"
                      @click="sendDownloadEvent(false, false)"
                    >
                      {{ $t('visitors.success_page.one_policy.download_receipt_button') }}
                    </ButtonQC>
                  </template>
                </div>
                <div v-else class="insurance-success__download-buttons">
                  <ButtonQC
                    icon="file-download"
                    variant="secondary"
                    size="large"
                    wide
                    data-test="download-all-policies-button"
                    :href="getFilesAsArchiveFor('policy')"
                    :target="getLinkTarget()"
                    @click="sendDownloadEvent(true, true)"
                  >
                    {{ $t('load_all_policies') }}
                  </ButtonQC>
                  <ButtonQC
                    icon="file"
                    variant="secondary"
                    size="large"
                    wide
                    data-test="download-all-receipts-button"
                    :href="getFilesAsArchiveFor('receipt')"
                    :target="getLinkTarget()"
                    @click="sendDownloadEvent(false, true)"
                  >
                    {{ $t('load_all_receipts') }}
                  </ButtonQC>
                </div>
              </template>
              <InboundPolicies
                v-else
                :name="policies[0].name"
                :inbound-policy-link="inboundPolicy"
                :inbound-receipt-link="inboundReceipt"
                :policy-link="multiplePolicies ? getFilesAsArchiveFor('policy') : policies[0].link"
                :receipt-link="
                  multiplePolicies ? getFilesAsArchiveFor('receipt') : policies[0].receiptlink
                "
                :is-multiple="multiplePolicies"
                @download-policy="
                  multiplePolicies ? sendDownloadEvent(true, true) : sendDownloadEvent()
                "
                @download-receipt="
                  multiplePolicies
                    ? sendDownloadEvent(false, true)
                    : sendDownloadEvent(false, false)
                "
                @download-inbound-policy="sendDownloadEvent(true, false, 'inbound_addon_block')"
                @download-inbound-receipt="sendDownloadEvent(false, false, 'inbound_addon_block')"
              />
            </template>
          </div>
        </FlowCard>

        <FlowCard
          v-if="multiplePolicies"
          :max-width="containerMaxWidth"
          :with-button="false"
          class="insurance__policies-wrapper"
        >
          <div class="insurance__policies-header heading-3">
            {{
              inboundPolicy
                ? $t('visitors.success_page.health_block.header')
                : $t('qic_policies_list.policies_list.header')
            }}
          </div>
          <div class="insurance__policies">
            <VisitorPolicy
              v-for="policy in policies"
              :key="policy.link"
              :name="policy.name"
              :email="policy.email"
              :policy-link="policy.link"
              :receipt-link="policy.receiptlink"
              :is-inbound="!!inboundPolicy"
              @download-policy="sendDownloadEvent"
              @download-receipt="sendDownloadEvent(false, false)"
            />
          </div>
        </FlowCard>

        <FlowCard
          v-if="isTravelKitSectionVisible"
          :max-width="600"
          :with-button="false"
          padding="var(--spacing-8)"
        >
          <TravelKit :policy-id="policyno" />
        </FlowCard>

        <DomainAnnotation v-if="!isPolicePaidWithProblems" />
      </div>

      <BaseError
        v-else
        :message="errorMessage"
        :error504="error504"
        :policy-id="policyno || undefined"
        :quote-id="quoteno || undefined"
        @reload="onReload"
      />
    </FlowWidget>
    <RedirectLoader v-if="loading" />
  </div>
</template>

<style lang="scss" scoped>
  .insurance-success {
    height: 100%;
  }
  .insurance-success__text {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-5);
  }

  .insurance-success-uninsured {
    background-color: var(--colorBgSrfSecondary);
    color: var(--colorTxtSrfPrimary);
    border-radius: var(--radius-6);
    padding: var(--spacing-7);
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: var(--spacing-5);
    word-break: break-all;
  }

  @media (max-width: 550px) {
    .btnInsSuc--outline {
      margin-top: 16px;
    }
  }

  .insurance {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: var(--spacing-8);

    &__policies-header {
      color: var(--colorTxtSrfPrimary);
    }

    &__policies {
      .visitor-policy + .visitor-policy {
        border-bottom: none;
      }
    }
  }

  .insurance-single-policy {
    display: flex;
    gap: var(--spacing-4);
    flex-direction: column;

    & > button {
      flex: 1;
    }
  }

  .insurance__policies-wrapper {
    gap: 0;
    padding: var(--spacing-8) var(--spacing-8) 0;

    @media (max-width: 568px) {
      padding: var(--spacing-8) var(--spacing-5) 0;
    }
  }

  .insurance-success__download-buttons {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-4);
  }
</style>

<style lang="scss">
  .insurance-success {
    .insurance-success__wrapper {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: var(--spacing-8);
      width: 100%;
    }

    .insurance-success__title {
      color: var(--colorTxtSrfPrimary);
      text-align: center;
    }

    &__icon {
      width: 84px;
      height: 84px;
      background-color: #eaf8ed;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto;
    }

    .insurance-success__email {
      display: flex;
      flex-wrap: wrap;
      color: var(--colorTxtSrfSecondary);
      text-align: center;
      justify-content: center;
    }

    .insurance-success__email-text {
      color: var(--colorTxtSrfPrimary);
    }
  }
</style>
